import { Button } from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useEffect, useRef } from 'react';

import { onMissingScope } from '../../api/scopeErrorBridge';
import { useApi } from '../../api/useApi';

/**
 * Listens for 403 `missing_scope` errors emitted by the axios interceptor
 * and surfaces a uniform snackbar with an "Autorizar" CTA.
 *
 * Must be rendered under:
 *   - SnackbarProvider (notistack) — for useSnackbar
 *   - UserProvider                  — useApi depends on the refreshToken context
 *
 * Renders nothing visible; it only wires up the bridge -> snackbar flow.
 */
export function MissingScopeHandler() {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const api = useApi();

  // Deduplicate consecutive notifications for the same integration/scope.
  const lastKeyRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onMissingScope(async (detail) => {
      const { integration_id, required_scope, bundle_hint } = detail || {};
      if (!integration_id || !bundle_hint) return;

      const dedupeKey = `${integration_id}:${required_scope}`;
      if (lastKeyRef.current === dedupeKey) return;
      lastKeyRef.current = dedupeKey;
      // Clear the dedupe lock after 8s so retries still show a new snackbar.
      setTimeout(() => {
        if (lastKeyRef.current === dedupeKey) lastKeyRef.current = null;
      }, 8000);

      const handleAuthorize = async (snackKey) => {
        try {
          const result = await api.expandMarketingScopes(
            integration_id,
            bundle_hint,
          );
          if (result?.auth_url) {
            window.location.href = result.auth_url;
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Error expanding marketing scopes:', err);
          enqueueSnackbar(
            err?.response?.data?.error ||
              'Error al solicitar permisos adicionales.',
            { variant: 'error' },
          );
        } finally {
          closeSnackbar(snackKey);
        }
      };

      enqueueSnackbar(
        `Esta acción requiere permiso adicional de Meta: ${required_scope}.`,
        {
          variant: 'warning',
          persist: true,
          action: (key) => (
            <>
              <Button
                size="small"
                color="inherit"
                onClick={() => handleAuthorize(key)}
              >
                Autorizar
              </Button>
              <Button
                size="small"
                color="inherit"
                onClick={() => closeSnackbar(key)}
              >
                Cerrar
              </Button>
            </>
          ),
        },
      );
    });

    return unsubscribe;
  }, [api, enqueueSnackbar, closeSnackbar]);

  return null;
}

export default MissingScopeHandler;
